import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSocket } from "../../../../hooks/useSocket";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { RootState } from "../../../../store/store";
import { useSelector } from "react-redux";
import dailyRecordService from "../../../../services/dailyRecord/dailyRecord.service";
import { CompanyActivityDto } from "../../../../types/attendanceDto.types";
import { Modal, Button, message, Avatar } from "antd";
import TicketService from "../../../../services/ticket/ticket.service";
import { useTranslation } from "react-i18next";
import { getFileUrl } from "../../../../utils/file.utils";
import { UserOutlined } from "@ant-design/icons";
import { all_routes } from "../../../../router/all_routes";
import { handleApiError } from "../../../../utils/errorHandler";

interface TicketStatusProps {
  selectedDepartments: number[];
  selectedBranches: number[];
}

const TicketStatus: React.FC<TicketStatusProps> = ({
  selectedDepartments, selectedBranches 
}) => {
  const [activities, setActivities] = useState<CompanyActivityDto[]>([]);
  const { connected, on } = useSocket();
  const {companyId} = useSelector((state: RootState) => state.auth);
  const [activeTicketCount, setActiveTicketCount] = useState<number>(0);
  const [selectedTicket, setSelectedTicket] = useState<CompanyActivityDto | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const routes = all_routes;
  const { t } = useTranslation();

  const isUserMatchesFilter = (user: any) => {
    const userDepartmentIds = user?.department?.map((d: any) => d.id) || [];
    const userBranchIds = user?.branch?.map((b: any) => b.id) || [];

    const departmentMatch = selectedDepartments.length === 0 || 
      selectedDepartments.some(id => userDepartmentIds.includes(id));

    const branchMatch = selectedBranches.length === 0 || 
      selectedBranches.some(id => userBranchIds.includes(id));

    return departmentMatch && branchMatch;
  };

  useEffect(() => {
    if (companyId) {
      const fetchPendingTickets = async () => {
        try {
          // Tüm ticket'ları çek
          const response = await TicketService.getAllTickets(Number(companyId));
          
          // Sadece pending olan ticket'ları filtrele ve CompanyActivityDto formatına çevir
          const pendingTickets = response.tickets
            .filter((ticket: any) => ticket.status === 'pending')
            .filter((ticket: any) => isUserMatchesFilter(ticket.user))
            .map((ticket: any) => ({
              id: ticket.id,
              type: "ticket_request",
              user: ticket.user,
              description: ticket.description,
              createdAt: ticket.createdAt,
              extraData: {
                ticketId: ticket.id,
                status: ticket.status
              }
            } as CompanyActivityDto));
          
          // Tarihe göre sırala (en yeni önce)
          const sortedActivities = pendingTickets.sort(
            (a: CompanyActivityDto, b: CompanyActivityDto) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          
          setActivities(sortedActivities);
          setActiveTicketCount(sortedActivities.length);
        } catch (error: any) {
          handleApiError(error,t);
        }
      };
      
      fetchPendingTickets();
    }
  }, [companyId, selectedDepartments, selectedBranches]);

  useEffect(() => {
    if (connected) {
      const handleNewActivity = (data: CompanyActivityDto) => {
        if (data.type === "ticket_request" && isUserMatchesFilter(data.user)) {
          
          setActivities(prev => {
            if (prev.some(a => a.id === data.id)) {
              return prev;
            }
            
            const newActivities = [data, ...prev];
            setActiveTicketCount(newActivities.length);
            return newActivities;
          });
        }
      };

      on("newActivity", handleNewActivity);
    }
  }, [connected, on, selectedDepartments, selectedBranches]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const showTicketModal = (activity: CompanyActivityDto) => {
    setSelectedTicket(activity);
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setSelectedTicket(null);
    setIsModalVisible(false);
  };

  const handleApproveTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      setIsLoading(true);
      const response = await TicketService.approveTicket(selectedTicket?.extraData?.ticketId as number);
      if (response) {
        message.success("Talep başarıyla onaylandı");
        // Pending ticket'ları yeniden yükle
        if (companyId) {
          const ticketResponse = await TicketService.getAllTickets(Number(companyId));
          const pendingTickets = ticketResponse.tickets
            .filter((ticket: any) => ticket.status === 'pending')
            .filter((ticket: any) => isUserMatchesFilter(ticket.user))
            .map((ticket: any) => ({
              id: ticket.id,
              type: "ticket_request",
              user: ticket.user,
              description: ticket.description,
              createdAt: ticket.createdAt,
              extraData: {
                ticketId: ticket.id,
                status: ticket.status
              }
            } as CompanyActivityDto));
          
          const sortedActivities = pendingTickets.sort(
            (a: CompanyActivityDto, b: CompanyActivityDto) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setActivities(sortedActivities);
          setActiveTicketCount(sortedActivities.length);
        }
        hideModal();
      }
    } catch (error: any) {
      handleApiError(error,t);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      setIsLoading(true);
      const response = await TicketService.rejectTicket(selectedTicket?.extraData?.ticketId as number);
      if (response) {
        message.success("Talep başarıyla reddedildi");
        // Pending ticket'ları yeniden yükle
        if (companyId) {
          const ticketResponse = await TicketService.getAllTickets(Number(companyId));
          const pendingTickets = ticketResponse.tickets
            .filter((ticket: any) => ticket.status === 'pending')
            .filter((ticket: any) => isUserMatchesFilter(ticket.user))
            .map((ticket: any) => ({
              id: ticket.id,
              type: "ticket_request",
              user: ticket.user,
              description: ticket.description,
              createdAt: ticket.createdAt,
              extraData: {
                ticketId: ticket.id,
                status: ticket.status
              }
            } as CompanyActivityDto));
          
          const sortedActivities = pendingTickets.sort(
            (a: CompanyActivityDto, b: CompanyActivityDto) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setActivities(sortedActivities);
          setActiveTicketCount(sortedActivities.length);
        }
        hideModal();
      }
    } catch (error: any) {
      handleApiError(error,t);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="col-xxl-4 col-xl-6 d-flex">
      <div className="card flex-fill">
        <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
          <h5 className="mb-2">{t("dashboard.ticketRequests.title")}</h5>
        </div>
        <div className="card-body">
          <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '10px' }}>
            {activities.length > 0 ? (
              activities.map((activity) => (
                <div 
                  key={activity.id} 
                  className="ticket-item d-flex align-items-center justify-content-between mb-3 p-2 border border-dashed br-5"
                  onClick={() => showTicketModal(activity)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center">
                  <Link to={`${routes.employeedetails.replace(':id', activity?.user?.id.toString())}`} className="avatar flex-shrink-0">
                    {activity?.user?.profilePicture ? (
                      <Avatar src={getFileUrl(activity?.user?.profilePicture)} size="large" />
                    ) : (
                      <Avatar src={activity?.user?.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'} size="default" />
                    )}
                  </Link>
                    <div className="ms-2">
                      <h6 className="fs-14 fw-medium text-truncate">
                        {activity.user.firstName + " " + activity.user.lastName}
                      </h6>
                      <p className="fs-13">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="link-default me-2">
                      <i className="ti ti-ticket" />
                    </span>
                    <span className={`fs-10 fw-medium d-inline-flex align-items-center badge ${
                      activity.extraData?.status === 'pending' ? 'badge-warning' : 
                      activity.extraData?.status === 'approved' ? 'badge-success' : 
                      activity.extraData?.status === 'rejected' ? 'badge-danger' : 'badge-secondary'
                    }`}>
                      <i className="ti ti-circle-filled fs-5 me-1" />
                      {formatTime(activity.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-3">
                <p className="mb-0">{t("dashboard.ticketRequests.noRequests")}</p>
              </div>
            )}
          </div>
          
          <Link
            to={routes.ticketAdmin}
            className="btn btn-light btn-md w-100 mt-2"
          >
            {t("dashboard.ticketRequests.allRequests")}
          </Link>
        </div>
      </div>

      {/* Ticket Detay Modal */}
      <Modal
        title={
          <div className="d-flex align-items-center border-bottom pb-3">
            <div className="d-flex align-items-center flex-grow-1">
              <span className="badge bg-warning me-3" style={{ fontSize: '1rem', padding: '8px 12px' }}>
                #{selectedTicket?.id}
              </span>
              <h5 className="mb-0">{selectedTicket?.description}</h5>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={hideModal}
        footer={[
          <Button key="close" onClick={hideModal}>
            Kapat
          </Button>,
          selectedTicket?.extraData?.status === 'pending' && (
            <>
              <Button
                key="reject"
                danger
                loading={isLoading}
                onClick={handleRejectTicket}
              >
                Reddet
              </Button>
              <Button
                key="approve"
                type="primary"
                loading={isLoading}
                onClick={handleApproveTicket}
              >
                Onayla
              </Button>
            </>
          )
        ]}
        width={700}
      >
        <div className="ticket-details-modal">
          <div className="user-info mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center">
              <div className="avatar-wrapper me-3">
                <ImageWithBasePath
                  src={selectedTicket?.user.avatar || "assets/img/profiles/avatar-01.jpg"}
                  alt="User"
                  className="avatar avatar-md rounded-circle border border-2 shadow-sm"
                />
              </div>
              <div className="user-details flex-grow-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">{selectedTicket?.user.firstName} {selectedTicket?.user.lastName}</h6>
                    <p className="text-muted mb-0 small">{selectedTicket?.user.email}</p>
                  </div>
                  <div className="text-end">
                    <p className="mb-0 text-muted small">
                      <i className="ti ti-calendar me-1"></i> 
                      {selectedTicket && new Date(selectedTicket.createdAt).toLocaleDateString('tr-TR', { 
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="mb-0 text-muted small">
                      <i className="ti ti-clock me-1"></i>
                      {selectedTicket && new Date(selectedTicket.createdAt).toLocaleTimeString('tr-TR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="ticket-content">
            <div className="section mb-4">
              <h6 className="text-primary mb-3">
                <i className="ti ti-file-description me-2"></i>
                {t("dashboard.ticketRequests.description")}
              </h6>
              <div className="description p-3 border rounded bg-white">
                <p className="mb-0" style={{ lineHeight: '1.6' }}>
                  {selectedTicket?.description || t("dashboard.ticketRequests.noDescription")}
                </p>
              </div>
            </div>

            <div className="section mb-4">
              <h6 className="text-primary mb-3">
                <i className="ti ti-info-circle me-2"></i>
                {t("dashboard.ticketRequests.statusInfo")}
              </h6>
              <div className="status-info p-3 border rounded bg-white">
                <div className="d-flex align-items-center">
                  <span 
                    className={`status-indicator ${selectedTicket?.extraData?.status === 'pending' ? 'bg-warning' : selectedTicket?.extraData?.status === 'approved' ? 'bg-success' : 'bg-danger'} me-2`}
                    style={{ width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block' }}
                  ></span>
                  <span className="status-text">
                    {selectedTicket?.extraData?.status === 'pending' && t("dashboard.ticketRequests.pending")}
                    {selectedTicket?.extraData?.status === 'approved' && t("dashboard.ticketRequests.approved")}
                    {selectedTicket?.extraData?.status === 'rejected' && t("dashboard.ticketRequests.rejected")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      
    </div>
  );
};

export default TicketStatus; 